import { Injectable } from '@angular/core';
import { Validators, ValidatorFn, AbstractControl, UntypedFormGroup } from '@angular/forms';
import { NgxMaskPipe } from 'ngx-mask';

import { whitespaceValidator } from '@reservation/validators/whitespace-validator';
import {
  TRAVELER_FORM_INPUTS, INVALID, PERSON_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { isValidCPF } from '@reservation/validators/cpf-validator';
import { isValidCNPJ } from '@reservation/validators/cnpj-validator';
import { isValidCUIT } from '@reservation/validators/cuit-validator';
import { isValidCUIL } from '@reservation/validators/cuil-validator';
import { InputMask, InputRegex } from '@forms/form-text-input/entities/input-mask';
import { isValidPeruRUC } from '@reservation/validators/ruc-validators';
import {
  BookingBillingDocumentValidations, BookingPaxDocumentValidations, BookingPaxTypeDocumentProps
} from '@app/entities/booking-documents';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { BookingDocumentBillingIdType } from '@app/entities/booking-billing-documents-id';
import { travelerFormDocumentValidators } from '@forms/mappers/form-document-input.mapper';

@Injectable()
export class FormValidationService {

  constructor(
    private readonly maskPipe: NgxMaskPipe
  ) { }

  get secondNameValidators(): ValidatorFn[] {
    return [Validators.maxLength(80), Validators.pattern(InputRegex.ALPHA)];
  }

  get stringValidators(): ValidatorFn[] {
    return [
      Validators.required, Validators.minLength(2), Validators.maxLength(80),
      whitespaceValidator, Validators.pattern(InputRegex.ALPHA)
    ];
  }

  get addressValidator(): ValidatorFn[] {
    return [
      Validators.required, Validators.minLength(2),
      Validators.maxLength(80)
    ];
  }

  get documentValidator(): ValidatorFn[] {
    return [
      Validators.required, whitespaceValidator,
      Validators.minLength(3)
    ];
  }

  public validateCPF(control: AbstractControl): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      if (isValidCPF(control.value)) return null;
      else return { [BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE]: true };
    };
  }

  public validateCNPJ(control: AbstractControl): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      if (isValidCNPJ(control.value)) return null;
      else return { [BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE]: true };
    };
  }

  public validateCUIL(control: AbstractControl): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      if (isValidCUIL(control.value, true)) return null;
      else return { [BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE]: true };
    };
  }

  public validateCUIT(control: AbstractControl): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      if (isValidCUIT(control.value, true)) return null;
      else return { [BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE]: true };
    };
  }

  public validatePeruRUC(control: AbstractControl): ValidatorFn {
    return (): { [key: string]: boolean } | null => {
      if (isValidPeruRUC(control.value, true)) return null;
      else return { [BookingFormGeneralErrorTypes.INVALID_DOCUMENT_VALUE]: true };
    };
  }

  public setPaxDocumentValidators(
    documentType: BookingDocumentPaxIdType, form: UntypedFormGroup, validators: BookingPaxDocumentValidations
  ): void {
    const defaultValidators = [
      ...this.documentValidator, Validators.pattern(validators?.REGEX), Validators.maxLength(validators?.MAX_LENGTH),
    ];
    const validFn = this.getPaxValidatorFn(documentType, form);
    const finalValidators = validFn ? [...defaultValidators, validFn] : defaultValidators;
    form
      ?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER)
      ?.setValidators(finalValidators);
    form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER)?.updateValueAndValidity();
  }

  public setBillingDocumentValidators(
    form: UntypedFormGroup, documentType: BookingDocumentBillingIdType, validators: BookingBillingDocumentValidations
  ): void {
    const defaultValidators = [
      ...this.documentValidator, Validators.pattern(validators?.REGEX), Validators.maxLength(validators?.MAX_LENGTH),
    ];
    const validFn = this.getBillingValidatorFn(documentType, form);
    const finalValidators = validFn ? [...defaultValidators, validFn] : defaultValidators;
    form
      ?.get(PERSON_FORM_INPUTS.DOCUMENT_NUMBER)
      ?.setValidators(finalValidators);
    form?.get(PERSON_FORM_INPUTS.DOCUMENT_NUMBER)?.updateValueAndValidity();
  }

  public formInvalidControls(formValidation: UntypedFormGroup): boolean {
    const controls = formValidation.controls;
    for (const control in controls) {
      if (control !== TRAVELER_FORM_INPUTS.BONVOY_NUMBER && controls[control].status === INVALID) {
        return true;
      }
    }
    return false;
  }

  public applyOrRemoveDocumentMask(form: UntypedFormGroup, value: string, selector: string, mask?: InputMask): void {
    const control = form.get(selector);
    if (mask && control?.value !== value) {
      control.patchValue(this.maskPipe.transform(value, mask), { emitEvent: false });
    }
  }

  public updatePaxDocumentSchema(
    form: UntypedFormGroup, documents: BookingPaxTypeDocumentProps[], documentType: BookingDocumentPaxIdType
  ): void {
    if (form) {
      const paxValidator = travelerFormDocumentValidators(form, documents) as BookingPaxDocumentValidations;
      const documentNumber = form?.value?.[TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER];
      this.setPaxDocumentValidators(documentType, form, paxValidator);
      this.applyOrRemoveDocumentMask(
        form, documentNumber, TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER, paxValidator?.MASK
      );
    }
  }

  private getBillingValidatorFn(documentType: BookingDocumentBillingIdType, form: UntypedFormGroup): ValidatorFn {
    const item = form?.get(PERSON_FORM_INPUTS.DOCUMENT_NUMBER);
    switch (documentType) {
      case BookingDocumentBillingIdType.CPF:
        return this.validateCPF(item);
      case BookingDocumentBillingIdType.CNPJ:
        return this.validateCNPJ(item);
      case BookingDocumentBillingIdType.CUIL:
        return this.validateCUIL(item);
      case BookingDocumentBillingIdType.CUIT:
        return this.validateCUIT(item);
      case BookingDocumentBillingIdType.RUC_PE:
        return this.validatePeruRUC(item);
      default:
        return null;
    }
  }

  private getPaxValidatorFn(documentType: BookingDocumentPaxIdType, form: UntypedFormGroup): ValidatorFn {
    const item = form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER);
    switch (documentType) {
      case BookingDocumentPaxIdType.CPF:
        return this.validateCPF(item);
      case BookingDocumentPaxIdType.CUIL:
        return this.validateCUIL(item);
      default:
        return null;
    }
  }

}
